/**
 * Little star bursts over an element that was solved (PlayerElement.ShowStars
 * → Prefabs/Player/okStars2, a 5 s cone particle system at 2× speed: ~14
 * stars/s, speed 7, gravity 0.5, size 0→1→0, random spin).
 *
 * Rendered imperatively on a layer above the slide (no React state) with the
 * Web Animations API, so bursts never re-render the activity.
 */
import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from "react";
import { useStageScale } from "./drag";
import { libraryUrl } from "../data/media";

export type FxTarget = HTMLElement | { x: number; y: number } | null | undefined;

interface FxApi {
  /** Stars over an element (or a stage point). */
  stars: (target: FxTarget) => void;
}

const FxContext = createContext<FxApi>({ stars: () => undefined });
export const useFx = () => useContext(FxContext);

const COUNT = 14;
const DURATION = 1600; // ms (startLifetime 4 s at simulationSpeed 2, shortened for the web)

export function FxLayer({ children }: { children: ReactNode }) {
  const layer = useRef<HTMLDivElement>(null);
  const scale = useStageScale();
  const scaleRef = useRef(scale);
  scaleRef.current = scale;

  const stars = useCallback((target: FxTarget) => {
    const root = layer.current;
    if (!root || !target) return;
    let x: number, y: number;
    if (target instanceof HTMLElement) {
      const r = target.getBoundingClientRect();
      const lr = root.getBoundingClientRect();
      const s = scaleRef.current || 1;
      x = (r.left + r.width / 2 - lr.left) / s;
      y = (r.top + r.height / 2 - lr.top) / s;
    } else {
      x = target.x;
      y = target.y;
    }
    const src = libraryUrl("ui/sparkle.png");
    for (let i = 0; i < COUNT; i++) {
      const delay = (i / COUNT) * 700 * Math.random();
      const angle = (-90 + (Math.random() * 2 - 1) * 47) * (Math.PI / 180); // cone of 47° around "up"
      const speed = 500 + Math.random() * 400; // stage px / s
      const vx = Math.cos(angle) * speed;
      const vy = Math.sin(angle) * speed;
      const g = 900; // px / s²
      const size = 70 + Math.random() * 40;
      const spin = (Math.random() * 2 - 1) * 540;
      const img = document.createElement("img");
      img.src = src;
      img.alt = "";
      img.draggable = false;
      img.className = "fx-star";
      img.style.width = `${size}px`;
      img.style.height = `${size}px`;
      img.style.left = `${x - size / 2}px`;
      img.style.top = `${y - size / 2}px`;
      root.appendChild(img);
      const steps = 16;
      const frames: Keyframe[] = [];
      for (let k = 0; k <= steps; k++) {
        const p = k / steps;
        const t = (p * DURATION) / 1000;
        const dx = vx * t;
        const dy = vy * t + 0.5 * g * t * t;
        const sz = p < 0.3 ? p / 0.3 : 1 - (p - 0.3) / 0.7; // size curve 0 → 1 → 0
        frames.push({ transform: `translate(${dx}px, ${dy}px) rotate(${spin * p}deg) scale(${sz})`, opacity: p < 0.85 ? 1 : (1 - p) / 0.15 });
      }
      const anim = img.animate(frames, { duration: DURATION, delay, easing: "linear", fill: "both" });
      anim.onfinish = () => img.remove();
      anim.oncancel = () => img.remove();
    }
  }, []);

  const api = useMemo(() => ({ stars }), [stars]);
  return (
    <FxContext.Provider value={api}>
      {children}
      <div ref={layer} className="fx-layer" aria-hidden />
    </FxContext.Provider>
  );
}
